import { throwApiError } from "../../../api/api";
import { employeeApi } from "../../../api/employeeApi";

/**
 * Ponto do colaborador (feat/ponto). Rotas `/dp/me/*` — sempre com o
 * `employeeApi` (token do portal), nunca com o `api` do staff.
 */

export type ExpectedKind = "TRABALHO" | "FOLGA" | "FERIADO" | "LOJA_FECHADA" | "FERIAS" | "AFASTAMENTO" | "FALTA_JUSTIFICADA" | "SEM_ESCALA";
export type TimesheetDayKind = ExpectedKind | "FALTA";

export type EffectivePunch = {
  at: string;
  source: "ORIGINAL" | "AJUSTE";
  punchId?: string;
  adjustmentId?: string;
  nsr?: number;
  outsideStore?: boolean | null;
};

export type TimesheetTotals = {
  expectedMinutes: number;
  workedMinutes: number;
  paidMinutes: number;
  overtimeWorkdayMinutes: number;
  overtimeRestDayMinutes: number;
  nightClockMinutes: number;
  nightComputedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  absenceDays: number;
  justifiedAbsenceDays: number;
  dsrLostCount: number;
  workedDaysCount: number;
  businessDaysCount: number;
  restDaysCount: number;
  originalPunchCount: number;
  adjustedPunchCount: number;
  daysWithoutRoster: number;
  daysOutsideStore: number;
  maxConsecutiveAbsences: number;
  pendingAdjustments: number;
};

export type TimesheetDay = {
  id: string;
  day: string;
  kind: TimesheetDayKind;
  expectedSource: "ESCALA" | "PADRAO" | "NENHUMA";
  shift: "MANHA" | "NOITE" | null;
  startMinute: number | null;
  endMinute: number | null;
  breakMinutes: number;
  expectedMinutes: number;
  workedMinutes: number;
  overtimeWorkdayMinutes: number;
  overtimeRestDayMinutes: number;
  nightClockMinutes: number;
  nightComputedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  breakTakenMinutes: number;
  dsrLost: boolean;
  flags: string[];
  effectivePunches: EffectivePunch[];
};

export type MyTimesheetDay = TimesheetDay & {
  canRequestAdjustment: boolean;
  needsAttention: boolean;
  suggestedMarks: number[];
};

export type AdjustmentType = "INCLUSAO_MARCACAO" | "DESCONSIDERAR_MARCACAO" | "ABONO_FALTA" | "ABONO_ATRASO" | "ABONO_HORAS" | "ANULACAO";
export type AdjustmentReason =
  | "ESQUECIMENTO"
  | "DISPOSITIVO_INDISPONIVEL"
  | "SEM_CREDENCIAL"
  | "FALHA_CONEXAO"
  | "SISTEMA_INDISPONIVEL"
  | "MARCACAO_INDEVIDA"
  | "MARCACAO_ATRASADA"
  | "AUSENCIA_JUSTIFICADA"
  | "OUTRO";
export type AdjustmentStatus = "PENDENTE" | "APROVADO" | "REJEITADO";

export type MyAdjustment = {
  id: string;
  day: string;
  type: AdjustmentType;
  reasonCode: AdjustmentReason;
  reason: string | null;
  proposedAt: string | null;
  minutes: number | null;
  batchId: string | null;
  requestedByEmployee: boolean;
  status: AdjustmentStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  annulledBy?: { id: string } | null;
  targetPunch?: { nsr: number; punchedAt: string } | null;
};

export type SignatureStatus = "NAO_FECHADO" | "PENDENTE" | "ASSINADO" | "CONTESTADO" | "DESATUALIZADO";

/** Situação da assinatura do espelho (calculada no servidor). */
export type SignatureState = {
  status: SignatureStatus;
  canSign: boolean;
  signedAt: string | null;
  hashShort: string | null;
  lastContest: { text: string; createdAt: string } | null;
  daysPending: number | null;
  overdue: boolean;
};

export const SIGNATURE_LABELS: Record<SignatureStatus, string> = {
  NAO_FECHADO: "Espelho aberto",
  PENDENTE: "Aguardando assinatura",
  ASSINADO: "Assinado",
  CONTESTADO: "Contestado",
  DESATUALIZADO: "Assinatura desatualizada",
};

export type MySignaturePending = { year: number; month: number; status: SignatureStatus; daysPending: number | null; overdue: boolean };

export type MyTimesheet = {
  enabled: boolean;
  startDate: string | null;
  year: number;
  month: number;
  timesheet: {
    id: string;
    status: "ABERTO" | "FECHADO";
    closedAt: string | null;
    computedAt: string | null;
    signature: SignatureState;
    totals: TimesheetTotals;
    blockers: string[];
    warnings: string[];
    days: MyTimesheetDay[];
  } | null;
  adjustments?: MyAdjustment[];
};

export type MyAdjustmentPayload = {
  day: string;
  type: Exclude<AdjustmentType, "ANULACAO">;
  reasonCode: AdjustmentReason;
  reason?: string;
  minuteOfDay?: number;
  minutes?: number;
  targetPunchId?: string;
};

/** Motivos que o colaborador escolhe ("sem acesso ao app" e "sistema fora do ar" são do DP). */
export const MY_ADJUSTMENT_REASONS: Array<{ value: AdjustmentReason; label: string }> = [
  { value: "ESQUECIMENTO", label: "Esqueci de marcar" },
  { value: "DISPOSITIVO_INDISPONIVEL", label: "Celular sem bateria, quebrado ou sem crédito" },
  { value: "FALHA_CONEXAO", label: "Tentei marcar e não foi (sem internet)" },
  { value: "MARCACAO_ATRASADA", label: "Marquei depois do horário real" },
  { value: "MARCACAO_INDEVIDA", label: "Marcação errada ou repetida" },
  { value: "AUSENCIA_JUSTIFICADA", label: "Ausência justificada" },
  { value: "OUTRO", label: "Outro motivo" },
];

export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReason, string> = {
  ESQUECIMENTO: "Esquecimento",
  DISPOSITIVO_INDISPONIVEL: "Celular indisponível",
  SEM_CREDENCIAL: "Sem acesso ao app",
  FALHA_CONEXAO: "Falha de conexão",
  SISTEMA_INDISPONIVEL: "Sistema fora do ar",
  MARCACAO_INDEVIDA: "Marcação indevida",
  MARCACAO_ATRASADA: "Marcação atrasada",
  AUSENCIA_JUSTIFICADA: "Ausência justificada",
  OUTRO: "Outro",
};

export const ADJUSTMENT_TYPE_LABELS: Record<AdjustmentType, string> = {
  INCLUSAO_MARCACAO: "Incluir marcação",
  DESCONSIDERAR_MARCACAO: "Desconsiderar marcação",
  ABONO_FALTA: "Abonar falta",
  ABONO_ATRASO: "Abonar atraso",
  ABONO_HORAS: "Abonar horas",
  ANULACAO: "Anulação",
};

export const DAY_KIND_LABELS: Record<TimesheetDayKind, string> = {
  TRABALHO: "Trabalho",
  FOLGA: "Folga",
  FERIADO: "Feriado",
  LOJA_FECHADA: "Loja fechada",
  FERIAS: "Férias",
  AFASTAMENTO: "Afastamento",
  FALTA: "Falta",
  FALTA_JUSTIFICADA: "Falta justificada",
  SEM_ESCALA: "—",
};

/** Avisos do dia em texto comum. */
export const DAY_FLAG_LABELS: Record<string, string> = {
  MARCACAO_IMPAR: "Falta uma marcação",
  INTERVALO_PRESUMIDO: "Intervalo descontado (só 2 marcações)",
  INTERVALO_INSUFICIENTE: "Intervalo menor que 1 hora",
  INTERJORNADA_VIOLADA: "Menos de 11h de descanso desde o dia anterior",
  HE_ACIMA_DO_LIMITE: "Mais de 2h extras no dia",
  TRABALHO_EM_DESCANSO: "Trabalho em dia de descanso",
  MARCACAO_EM_AUSENCIA: "Marcação em férias ou afastamento",
  FORA_DA_LOJA: "Marcação longe da loja",
  SEM_LOCALIZACAO: "Marcação sem localização",
  COM_AJUSTE: "Com ajuste",
  SEM_ESCALA_PUBLICADA: "Sem escala publicada (vale a jornada padrão)",
  ESCALA_DIVERGENTE: "Escala diferente do cadastro",
  ESCALA_SEM_HORARIO: "Escala sem horário",
  FORA_DO_PERIODO: "Fora do contrato ou antes do ponto",
};

/** Minutos do dia → "HH:mm" (aceita > 1440: madrugada do dia seguinte). */
export const hhmm = (minute: number | null | undefined) => {
  if (minute === null || minute === undefined) return "";
  const m = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/** Duração em minutos → "7h20" / "45min". */
export const duration = (minutes: number) => {
  if (!minutes) return "0h";
  const sign = minutes < 0 ? "-" : "";
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (!h) return `${sign}${m}min`;
  return `${sign}${h}h${m ? String(m).padStart(2, "0") : ""}`;
};

/** Instante ISO → "HH:mm" no horário de Brasília. */
export const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" });

export const getMyTimesheetApi = async (year: number, month: number): Promise<MyTimesheet> => {
  try {
    const { data } = await employeeApi.get<MyTimesheet>(`/dp/me/timesheets/${year}/${month}`);
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar o seu espelho de ponto.");
    throw error;
  }
};

/** Sem `throwApiError` no 401: senha errada não pode parecer sessão expirada. */
export const signMyTimesheetApi = async (year: number, month: number, password: string): Promise<{ signedAt: string; hashShort: string }> => {
  try {
    const { data } = await employeeApi.post(`/dp/me/timesheets/${year}/${month}/sign`, { password });
    return data;
  } catch (error: any) {
    if (error?.response?.status === 401) throw new Error("Senha incorreta.");
    throwApiError(error, "Não foi possível assinar o espelho.");
    throw error;
  }
};

export type SignManyResult = {
  signed: Array<{ year: number; month: number; signedAt: string; hashShort: string }>;
  skipped: Array<{ year: number; month: number; reason: string }>;
};

/** Vários meses com uma senha só. Mesmo tratamento do 401 da assinatura de um mês. */
export const signManyMyTimesheetsApi = async (months: Array<{ year: number; month: number }>, password: string): Promise<SignManyResult> => {
  try {
    const { data } = await employeeApi.post<SignManyResult>("/dp/me/timesheets/sign-many", { months, password });
    return data;
  } catch (error: any) {
    if (error?.response?.status === 401) throw new Error("Senha incorreta.");
    throwApiError(error, "Não foi possível assinar os espelhos.");
    throw error;
  }
};

export const contestMyTimesheetApi = async (year: number, month: number, text: string) => {
  try {
    const { data } = await employeeApi.post(`/dp/me/timesheets/${year}/${month}/contest`, { text });
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível enviar a contestação.");
    throw error;
  }
};

export const getMySignaturePendingApi = async (): Promise<MySignaturePending[]> => {
  try {
    const { data } = await employeeApi.get<MySignaturePending[]>("/dp/me/timesheets/signature-pending");
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível consultar as assinaturas pendentes.");
    throw error;
  }
};

export const getMyAdjustmentsApi = async (status?: AdjustmentStatus): Promise<MyAdjustment[]> => {
  try {
    const { data } = await employeeApi.get<MyAdjustment[]>("/dp/me/time-adjustments", { params: status ? { status } : {} });
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível carregar os seus pedidos.");
    throw error;
  }
};

export const createMyAdjustmentApi = async (payload: MyAdjustmentPayload) => {
  try {
    const { data } = await employeeApi.post("/dp/me/time-adjustments", payload);
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível enviar o pedido.");
    throw error;
  }
};

export const createMyDayAdjustmentApi = async (payload: { day: string; reasonCode: AdjustmentReason; reason?: string; marks: number[] }) => {
  try {
    const { data } = await employeeApi.post("/dp/me/time-adjustments/day", payload);
    return data;
  } catch (error) {
    throwApiError(error, "Não foi possível enviar o pedido.");
    throw error;
  }
};

export const cancelMyAdjustmentApi = async (id: string) => {
  try {
    await employeeApi.delete(`/dp/me/time-adjustments/${id}`);
  } catch (error) {
    throwApiError(error, "Não foi possível cancelar o pedido.");
    throw error;
  }
};
